// Shared helpers for the dropdown-driven join builders in the Schema and
// Business Area designers.

import type { SchemaTable } from './types'

/** Parse a simple `LT.COL = RT.COL` join condition (either direction) so the
 *  column dropdowns can reflect what the condition already says. */
export function parseSimpleJoin(
  cond: string, lt: string, rt: string,
): { lc: string; rc: string } {
  const m = /^\s*([A-Za-z0-9_]+)\.([A-Za-z0-9_]+)\s*=\s*([A-Za-z0-9_]+)\.([A-Za-z0-9_]+)\s*$/.exec(cond || '')
  if (!m) return { lc: '', rc: '' }
  const [, t1, c1, t2, c2] = m
  if (t1.toUpperCase() === (lt || '').toUpperCase() && t2.toUpperCase() === (rt || '').toUpperCase()) return { lc: c1, rc: c2 }
  if (t1.toUpperCase() === (rt || '').toUpperCase() && t2.toUpperCase() === (lt || '').toUpperCase()) return { lc: c2, rc: c1 }
  return { lc: '', rc: '' }
}

/** Column names of a table in the schema/area TABLES list. */
export function tableColumns(tables: SchemaTable[], tableName: string): string[] {
  const t = tables.find((x) => x.TABLE_NAME === (tableName || '').toUpperCase())
  return (t?.COLUMNS || []).map((c) => c.DBNAME).filter(Boolean)
}

export interface ColumnPair {
  lc: string
  rc: string
}

const PAIR_RE = /^\s*([A-Za-z0-9_]+)\.([A-Za-z0-9_]+)\s*=\s*([A-Za-z0-9_]+)\.([A-Za-z0-9_]+)\s*$/
const eq = (a: string, b: string) => (a || '').toUpperCase() === (b || '').toUpperCase()

/** Parse an ON condition into left/right column pairs, but ONLY when it is a
 *  clean set of `LT.col = RT.col` equalities AND-ed together (either direction
 *  per pair). Anything else (functions, non-equality, extra predicates) returns
 *  [] — the caller treats that as a free-text condition. */
export function parseColumnPairs(cond: string, lt: string, rt: string): ColumnPair[] {
  const c = (cond || '').trim()
  if (!c) return []
  const parts = c.split(/\s+AND\s+/i)
  const pairs: ColumnPair[] = []
  for (const part of parts) {
    const m = PAIR_RE.exec(part)
    if (!m) return []
    const [, t1, c1, t2, c2] = m
    if (eq(t1, lt) && eq(t2, rt)) pairs.push({ lc: c1, rc: c2 })
    else if (eq(t1, rt) && eq(t2, lt)) pairs.push({ lc: c2, rc: c1 })
    else return []
  }
  return pairs
}

/** Serialize column pairs back into `LT.a = RT.a AND LT.b = RT.b`. */
export function serializeColumnPairs(pairs: ColumnPair[], lt: string, rt: string): string {
  return pairs
    .filter((p) => p.lc && p.rc)
    .map((p) => `${lt}.${p.lc} = ${rt}.${p.rc}`)
    .join(' AND ')
}

/** Would this condition round-trip as clean column pairs? (i.e. can the
 *  dropdown/pair editor represent it, or must it be edited as free text). */
export function isPairExpressible(cond: string, lt: string, rt: string): boolean {
  const c = (cond || '').trim()
  if (!c) return true
  return parseColumnPairs(c, lt, rt).length > 0
}

// ════════════════════════════════════════════════════════════════════
// Multi-condition ON builder — condition SETS with AND/OR.
//
// The visual builder authors ON clauses like
//   (SALES.SITE_CODE = SITE.SITE_CODE OR SITE.SITE_CODE IS NULL)
//   AND SALES.DOC_TYPE = 'INV'
// as GROUPS of terms: each group combines its terms with its OWN and/or,
// groups combine with ONE outer and/or. JOIN_CONDITION (the flat SQL string
// the engines inline verbatim) stays the single source of truth — the model
// below is parsed FROM and serialized BACK TO that string, covering exactly
// the grammar the builder can author. Anything richer (functions, nested
// parens, subqueries) parses to null → the editor falls back to free text.
// ════════════════════════════════════════════════════════════════════

/** Operators a term may use; the IS [NOT] NULL pair takes no right operand. */
export const JOIN_TERM_OPS = ['=', '!=', '<>', '>', '>=', '<', '<=', 'LIKE', 'IS NULL', 'IS NOT NULL'] as const
export const NULLARY_JOIN_OPS = new Set(['IS NULL', 'IS NOT NULL'])

export interface JoinTerm {
  /** Left operand — always a column ref "TABLE.COL". */
  left: string
  op: string
  /** Right operand — "TABLE.COL" (rightKind 'col') or a raw literal token
   *  ('Y', 42 — kept verbatim, quoted at serialize); '' for IS [NOT] NULL. */
  right: string
  rightKind: 'col' | 'lit'
}

export interface JoinConditionGroup {
  /** Combinator INSIDE the group. */
  condition: 'and' | 'or'
  terms: JoinTerm[]
}

export interface JoinConditionModel {
  /** Combinator BETWEEN groups. */
  joiner: 'and' | 'or'
  groups: JoinConditionGroup[]
}

export function blankJoinTerm(): JoinTerm {
  return { left: '', op: '=', right: '', rightKind: 'col' }
}

export function blankJoinGroup(): JoinConditionGroup {
  // Inside a group OR is the point (alternatives); groups join with AND.
  return { condition: 'or', terms: [blankJoinTerm()] }
}

const COLREF_SRC = '[A-Za-z_][A-Za-z0-9_]*\\.[A-Za-z_][A-Za-z0-9_]*'
const COLREF_ONLY_RE = new RegExp(`^${COLREF_SRC}$`)
const NULL_TERM_RE = new RegExp(`^(${COLREF_SRC})\\s+IS\\s+(NOT\\s+)?NULL$`, 'i')
const LIKE_TERM_RE = new RegExp(
  `^(${COLREF_SRC})\\s+LIKE\\s+(${COLREF_SRC}|'(?:[^']|'')*')$`, 'i')
const CMP_TERM_RE = new RegExp(
  `^(${COLREF_SRC})\\s*(=|!=|<>|>=|<=|>|<)\\s*(${COLREF_SRC}|'(?:[^']|'')*'|[0-9]+(?:\\.[0-9]+)?)$`)

/** Split at depth-0 AND/OR (outside quotes/parens). All combinators at one
 *  level must agree (mixed unparenthesised and/or → null: ambiguous). */
function splitTop(expr: string): { parts: string[]; joiner: 'and' | 'or' } | null {
  const s = expr
  const parts: string[] = []
  let depth = 0
  let cur = ''
  let joiner: 'and' | 'or' | null = null
  let i = 0
  while (i < s.length) {
    const ch = s[i]
    if (ch === "'") {
      // consume the quoted string (with '' escapes) atomically
      let j = i + 1
      while (j < s.length) {
        if (s[j] === "'" && s[j + 1] === "'") { j += 2; continue }
        if (s[j] === "'") break
        j += 1
      }
      cur += s.slice(i, Math.min(j + 1, s.length))
      i = j + 1
      continue
    }
    if (ch === '(') { depth += 1; cur += ch; i += 1; continue }
    if (ch === ')') { depth -= 1; if (depth < 0) return null; cur += ch; i += 1; continue }
    if (depth === 0 && /\s/.test(s[i - 1] || ' ')) {
      const m = /^(AND|OR)(?=[\s(])/i.exec(s.slice(i))
      if (m) {
        const found = m[1].toLowerCase() as 'and' | 'or'
        if (joiner === null) joiner = found
        else if (joiner !== found) return null
        if (!cur.trim()) return null
        parts.push(cur.trim())
        cur = ''
        i += m[1].length
        continue
      }
    }
    cur += ch
    i += 1
  }
  if (depth !== 0) return null
  if (cur.trim()) parts.push(cur.trim())
  if (!parts.length) return null
  return { parts, joiner: joiner ?? 'and' }
}

/** Strip parens that wrap the WHOLE expression (repeatedly). */
function stripWrapParens(expr: string): string {
  let t = expr.trim()
  for (;;) {
    if (!t.startsWith('(') || !t.endsWith(')')) return t
    let depth = 0
    for (let i = 0; i < t.length; i += 1) {
      if (t[i] === '(') depth += 1
      else if (t[i] === ')') {
        depth -= 1
        if (depth === 0 && i < t.length - 1) return t // closes early — not a wrap
      }
    }
    t = t.slice(1, -1).trim()
  }
}

function parseTerm(raw: string): JoinTerm | null {
  const t = stripWrapParens(raw)
  let m = NULL_TERM_RE.exec(t)
  if (m) {
    return { left: m[1], op: m[2] ? 'IS NOT NULL' : 'IS NULL', right: '', rightKind: 'lit' }
  }
  m = LIKE_TERM_RE.exec(t)
  if (m) {
    const right = m[2]
    return {
      left: m[1], op: 'LIKE', right,
      rightKind: COLREF_ONLY_RE.test(right) ? 'col' : 'lit',
    }
  }
  m = CMP_TERM_RE.exec(t)
  if (m) {
    const right = m[3]
    return {
      left: m[1], op: m[2], right,
      rightKind: COLREF_ONLY_RE.test(right) ? 'col' : 'lit',
    }
  }
  return null
}

/** Parse an ON condition into the builder's condition-set model. Returns null
 *  for anything beyond the builder's grammar (→ edit as free text). */
export function parseJoinCondition(cond: string): JoinConditionModel | null {
  const c = (cond || '').trim()
  if (!c) return null
  const top = splitTop(c)
  if (!top) return null
  const groups: JoinConditionGroup[] = []
  for (const part of top.parts) {
    const inner = splitTop(stripWrapParens(part))
    if (!inner) return null
    const terms: JoinTerm[] = []
    for (const rawTerm of inner.parts) {
      const term = parseTerm(rawTerm)
      if (!term) return null
      terms.push(term)
    }
    groups.push({ condition: inner.joiner, terms })
  }
  return { joiner: top.joiner, groups }
}

/** Quote a literal token for SQL: numbers stay bare; already-quoted strings
 *  pass through; anything else is single-quoted with '' escaping. */
export function quoteJoinLiteral(raw: string): string {
  const s = (raw || '').trim()
  if (/^[0-9]+(\.[0-9]+)?$/.test(s)) return s
  if (/^'(?:[^']|'')*'$/.test(s)) return s
  return `'${s.replace(/'/g, "''")}'`
}

function termComplete(t: JoinTerm): boolean {
  if (!t.left) return false
  if (NULLARY_JOIN_OPS.has((t.op || '').toUpperCase())) return true
  return !!(t.right || '').trim()
}

function serializeTerm(t: JoinTerm): string {
  const op = (t.op || '=').toUpperCase()
  if (NULLARY_JOIN_OPS.has(op)) return `${t.left} ${op}`
  const right = t.rightKind === 'lit' ? quoteJoinLiteral(t.right) : t.right
  return `${t.left} ${op} ${right}`
}

/** Serialize the condition-set model back to the flat ON string the engines
 *  inline. Single-term groups need no parens, so plain equality chains
 *  round-trip byte-identical to the legacy pair editor's output. */
export function serializeJoinCondition(model: JoinConditionModel): string {
  const parts: string[] = []
  for (const g of model.groups || []) {
    const terms = (g.terms || []).filter(termComplete).map(serializeTerm)
    if (!terms.length) continue
    const glue = ` ${(g.condition === 'and' ? 'AND' : 'OR')} `
    parts.push(terms.length > 1 ? `(${terms.join(glue)})` : terms[0])
  }
  return parts.join(` ${(model.joiner === 'or' ? 'OR' : 'AND')} `)
}
